/**
 * Normalizes text for ElevenLabs TTS to improve speech quality.
 * Handles Markdown formatting, special characters, and technical acronyms.
 */

// Polish number words
const POLISH_ONES = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
const POLISH_TEENS = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
const POLISH_TENS = ['', 'dziesięć', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
const POLISH_HUNDREDS = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];

/**
 * Converts a number (0-999999) to Polish words
 */
function numberToPolishWords(num: number): string {
  if (num === 0) return 'zero';
  if (num < 0) return 'minus ' + numberToPolishWords(-num);
  
  let result = '';
  
  // Thousands
  if (num >= 1000) {
    const thousands = Math.floor(num / 1000);
    if (thousands === 1) {
      result += 'tysiąc ';
    } else if (thousands >= 2 && thousands <= 4) {
      result += numberToPolishWords(thousands) + ' tysiące ';
    } else if (thousands >= 5 && thousands <= 21) {
      result += numberToPolishWords(thousands) + ' tysięcy ';
    } else {
      const lastDigit = thousands % 10;
      const lastTwoDigits = thousands % 100;
      if (lastTwoDigits >= 12 && lastTwoDigits <= 14) {
        result += numberToPolishWords(thousands) + ' tysięcy ';
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        result += numberToPolishWords(thousands) + ' tysiące ';
      } else {
        result += numberToPolishWords(thousands) + ' tysięcy ';
      }
    }
    num = num % 1000;
  }
  
  // Hundreds
  if (num >= 100) {
    result += POLISH_HUNDREDS[Math.floor(num / 100)] + ' ';
    num = num % 100;
  }
  
  // Tens and ones
  if (num >= 20) {
    result += POLISH_TENS[Math.floor(num / 10)] + ' ';
    num = num % 10;
    if (num > 0) {
      result += POLISH_ONES[num] + ' ';
    }
  } else if (num >= 10) {
    result += POLISH_TEENS[num - 10] + ' ';
  } else if (num > 0) {
    result += POLISH_ONES[num] + ' ';
  }
  
  return result.trim();
}

/**
 * Converts numbers with units to Polish spoken form
 */
function convertNumbersWithUnits(text: string): string {
  let result = text;
  
  // Pattern: numbers with m² (square meters)
  result = result.replace(/(\d[\d\s]*)\s*m²/gi, (match, numStr) => {
    const num = parseInt(numStr.replace(/\s/g, ''));
    if (isNaN(num)) return match;
    return numberToPolishWords(num) + ' metrów kwadratowych';
  });
  
  // Pattern: numbers with km² 
  result = result.replace(/(\d[\d\s]*)\s*km²/gi, (match, numStr) => {
    const num = parseInt(numStr.replace(/\s/g, ''));
    if (isNaN(num)) return match;
    return numberToPolishWords(num) + ' kilometrów kwadratowych';
  });
  
  // Pattern: numbers with % (percent)
  result = result.replace(/(\d[\d\s]*)\s*%/g, (match, numStr) => {
    const num = parseInt(numStr.replace(/\s/g, ''));
    if (isNaN(num)) return match;
    return numberToPolishWords(num) + ' procent';
  });
  
  // Pattern: standalone large numbers with spaces (e.g., "25 000" -> "dwadzieścia pięć tysięcy")
  result = result.replace(/\b(\d{1,3})\s+(\d{3})\b/g, (match, first, second) => {
    const num = parseInt(first + second);
    if (isNaN(num)) return match;
    return numberToPolishWords(num);
  });
  
  return result;
}

export function prepareTTSText(text: string): string {
  let result = text;
  
  // 1. Replace en-dash and em-dash with comma for natural pause
  result = result.replace(/[–—]/g, ',');
  
  // 2. Remove bullet points and special list markers
  result = result.replace(/[●•◦▪▸►▶■□○◆◇★☆✓✔✗✘→←↑↓]/g, '');
  
  // 3. Remove Markdown formatting
  result = result.replace(/\*\*/g, '');       // remove **bold**
  result = result.replace(/\*/g, '');          // remove *italic*
  result = result.replace(/_{1,2}/g, '');      // remove _underline_ or __bold__
  result = result.replace(/`{1,3}/g, '');      // remove `code` or ```code blocks```
  result = result.replace(/^#+\s*/gm, '');     // remove # headers
  result = result.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // convert [link](url) to just link text
  
  // 4. Replace multiple newlines with single space
  result = result.replace(/\n+/g, ' ');
  
  // 5. Remove multiple spaces
  result = result.replace(/\s{2,}/g, ' ');
  
  // 6. Convert numbers with units to Polish words (before acronym expansion)
  result = convertNumbersWithUnits(result);
  
  // 7. Expand common technical acronyms for better pronunciation
  // Use spaces between letters instead of dots (ElevenLabs best practice)
  result = result.replace(/\bRTLS\b/g, 'R T L S');
  result = result.replace(/\bERP\b/g, 'E R P');
  result = result.replace(/\bWMS\b/g, 'W M S');
  result = result.replace(/\bCRM\b/g, 'C R M');
  result = result.replace(/\bAPI\b/g, 'A P I');
  result = result.replace(/\bB2B\b/g, 'B2B'); // commonly pronounced as word
  result = result.replace(/\bB2C\b/g, 'B2C'); // commonly pronounced as word
  result = result.replace(/\bSEO\b/g, 'S E O');
  result = result.replace(/\bPPC\b/g, 'P P C');
  result = result.replace(/\bROI\b/g, 'R O I');
  result = result.replace(/\bKPI\b/g, 'K P I');
  result = result.replace(/\bIT\b/g, 'I T');
  result = result.replace(/\bHR\b/g, 'H R');
  result = result.replace(/\bPR\b/g, 'P R');
  result = result.replace(/\bAI\b/g, 'A I');
  result = result.replace(/\bML\b/g, 'M L');
  result = result.replace(/\bIoT\b/g, 'I o T');
  result = result.replace(/\bUX\b/g, 'U X');
  result = result.replace(/\bUI\b/g, 'U I');
  // SaaS is commonly pronounced as a word, leave as is
  
  // 8. Clean up any double periods that might have been created
  result = result.replace(/\.{2,}/g, '.');
  
  return result.trim();
}
